import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { Rate } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import NoImage from "../../../images/no-image.jpg"; // replace with your placeholder image
import { useNavigate, useParams } from "react-router-dom";
import {
  GoogleMap,
  Marker,
  Autocomplete,
  useJsApiLoader,
} from "@react-google-maps/api";
import Swal from "sweetalert2";
import PageTitle from "../../layouts/PageTitle";
import { Button } from "react-bootstrap";
import { getAccommodationType } from "../../../services/AccommodationTypeService";
import {
  createAccommodationDetails,
  getAccommodationDetailsById,
  updateAccommodationDetails,
} from "../../../services/AccommodationDetailsService";

const containerStyle = {
  width: "100%",
  height: "400px",
};

const center = {
  lat: 20.5937, // Default to India
  lng: 78.9629,
};

const apiKey = "xxxxxxxxxxxxxxxxxxxxxxxxxxxxx";

// AIzaSyCEW1Wc11SUHoF3sgsxEq0M1XgOqSelP5M

const AddAccommodationDetails = () => {
  const [mapCenter, setMapCenter] = useState(center);
  const [markerPosition, setMarkerPosition] = useState(null);
  const autocompleteRef = useRef(null);

  const [formData, setFormData] = useState({
    accommodation_title: "",
    accommodation_rating: 0,
    accommodation_desc: "",
    accommodation_rate: 0,
    accommodation_beds: [],
    accommodation_sleeps: "",
    accommodation_carpetarea: "",
    accommodation_venue: "",
    accommodation_venue_meta: "",
    accommodation_latitude: 20.5937,
    accommodation_longitude: 78.9629,
    type_id: null,
  });

  const onPlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (place.geometry) {
      const location = place.geometry.location;
      const lat = location.lat();
      const lng = location.lng();
      const address = place.formatted_address || place.name;
      const name = place.name || "";
      const placeId = place.place_id || "";
      const rating = place.rating || null;
      const userRatingsTotal = place.user_ratings_total || null;
      const phone = place.formatted_phone_number || "";
      const website = place.website || "";
      const types = place.types || [];
      const openingHours = place.opening_hours?.weekday_text || [];

      let venue_meta = {
        address,
        name,
        placeId,
        rating,
        userRatingsTotal,
        phone,
        website,
        types,
        openingHours,
      };

      setFormData((prev) => ({
        ...prev,
        accommodation_venue_meta: JSON.stringify(venue_meta),
        accommodation_venue: place.name + " " + place.formatted_address,
        accommodation_latitude: lat,
        accommodation_longitude: lng,
      }));
      setMapCenter({ lat, lng });
      setMarkerPosition({ lat, lng });
    }
  };

  const [accommodationImage, setAccommodationImage] = useState(null);
  const [existingImage, setExistingImage] = useState("");
  const [errors, setErrors] = useState({});
  const hasFetched = useRef(false);
  const hasDataFetched = useRef(false);
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [accommodationBeds, setAccommodationBeds] = useState([]);

  const navigate = useNavigate();

  // dropdown mock data
  const [accommodationTypeOptions, setAccommodationTypeOptions] = useState([]);
  const [accommodationTypePage, setAccommodationTypePage] = useState(1);
  const [accommodationTypeTotalPages, setAccommodationTypeTotalPages] =
    useState(1);
  const [accommodationTypeLoading, setAccommodationTypeLoading] =
    useState(false);

  const fetchAccommodationTypes = async (pageNumber = 1, query = "") => {
    if (pageNumber > accommodationTypeTotalPages) return; // no more pages
    try {
      setAccommodationTypeLoading(true);
      const limit = 5; // set per-page limit
      const response = await getAccommodationType(pageNumber, limit, query);
      const { data, pagination } = response.data;

      const options = (data || []).map((c) => ({
        value: c.id,
        label: `${c.title}`,
      }));

      if (pageNumber === 1) {
        // first page or search => replace
        setAccommodationTypeOptions(options);
      } else {
        // append for scroll
        setAccommodationTypeOptions((prev) => [...prev, ...options]);
      }

      setAccommodationTypePage(pagination?.page || pageNumber);
      setAccommodationTypeTotalPages(pagination?.totalPages || 1);
    } catch (error) {
      console.error("Error fetching accommodation types:", error);
    } finally {
      setAccommodationTypeLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (e) => {
    setAccommodationImage(e.target.files[0]);
  };

  const handleRatingChange = (value) => {
    setFormData({ ...formData, accommodation_rating: value });
  };

  const handleAddBed = () => {
    setAccommodationBeds((prevBeds) => [...prevBeds, { key: "", value: "" }]);
  };

  const handleRemoveBed = (index) => {
    const newBeds = accommodationBeds.filter((_, i) => i !== index);
    setAccommodationBeds(newBeds);
  };

  const handleBedChange = (index, field, value) => {
    const newBeds = [...accommodationBeds];
    newBeds[index][field] = value;
    setAccommodationBeds(newBeds);
  };

  const handleAddField = (bedIndex) => {
    const newBeds = [...accommodationBeds];
    newBeds[bedIndex].fields.push({ key: "", value: "" });
    setAccommodationBeds(newBeds);
  };

  const handleRemoveField = (bedIndex, fieldIndex) => {
    const newBeds = [...accommodationBeds];
    newBeds[bedIndex].fields.splice(fieldIndex, 1);
    setAccommodationBeds(newBeds);
  };

  const handleFieldChange = (bedIndex, fieldIndex, key, value) => {
    const newBeds = [...accommodationBeds];
    newBeds[bedIndex].fields[fieldIndex][key] = value;
    setAccommodationBeds(newBeds);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!id && !accommodationImage)
      newErrors.accommodation_image = "Accommodation image is required";
    if (!formData.accommodation_title.trim())
      newErrors.accommodation_title = "Accommodation title is required";
    if (!formData.accommodation_desc.trim())
      newErrors.accommodation_desc = "Accommodation description is required";
    if (!formData.accommodation_rate)
      newErrors.accommodation_rate = "Accommodation rate is required";
    if (!formData.accommodation_sleeps.trim())
      newErrors.accommodation_sleeps = "Accommodation sleeps is required";
    if (!formData.accommodation_carpetarea)
      newErrors.accommodation_carpetarea =
        "Accommodation carpet area is required";
    if (!formData.accommodation_venue)
      newErrors.accommodation_venue = "Accommodation Venue is required";
    if (!formData.type_id)
      newErrors.type_id = "Accommodation type is required";
    return newErrors;
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        // 1️⃣ Fetch dropdowns first
        const [accommodationTypeRes] = await Promise.all([
          getAccommodationType(1, 5000), // fetch all or enough for dropdown
        ]);

        const accommodationTypeOpts = (
          accommodationTypeRes.data?.data || []
        ).map((c) => ({
          value: c.id,
          label: c.title,
        }));
        setAccommodationTypeOptions(accommodationTypeOpts);

        // 2️⃣ Then fetch accommodation details by ID
        if (id) {
          const res = await getAccommodationDetailsById(id);
          if (res.data?.success) {
            const accommodation = res.data.data;
            setFormData({
              accommodation_title: accommodation.accommodation_title || "",
              accommodation_desc: accommodation.accommodation_desc || "",
              accommodation_rating:
                parseFloat(accommodation.accommodation_rating) || 0,
              accommodation_rate: accommodation.accommodation_rate || "",
              accommodation_sleeps: accommodation.accommodation_sleeps || "",
              accommodation_carpetarea:
                accommodation.accommodation_carpetarea || "",
              accommodation_venue: accommodation.accommodation_venue || "",
              accommodation_venue_meta:
                accommodation.accommodation_venue_meta || "",
              accommodation_latitude:
                accommodation.accommodation_latitude || "",
              accommodation_longitude:
                accommodation.accommodation_longitude || "",
              type_id:
                accommodationTypeOpts.find(
                  (c) => c.value === accommodation.type_id
                ) || null,
              accommodation_beds:
                JSON.parse(accommodation.accommodation_beds) || [],
            });
            setMarkerPosition({
              lat: parseFloat(accommodation?.accommodation_latitude) || 20.5937,
              lng:
                parseFloat(accommodation?.accommodation_longitude) || 78.9629,
            });
            setMapCenter({
              lat: parseFloat(accommodation?.accommodation_latitude) || 20.5937,
              lng:
                parseFloat(accommodation?.accommodation_longitude) || 78.9629,
            });
            setAccommodationBeds(accommodation?.accommodation_beds || []);
            setExistingImage(accommodation?.accommodation_image || "");
          } else {
            console.error("Failed to fetch accommodation by ID");
          }
        } else {
          setMarkerPosition({
            lat: 20.5937,
            lng: 78.9629,
          });
          setMapCenter({
            lat: 20.5937,
            lng: 78.9629,
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchData();
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      console.log("validationErrors", validationErrors);
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      const fd = new FormData();
      if (accommodationImage)
        fd.append("accommodation_image", accommodationImage);

      const submitData = { ...formData };

      // append normal fields
      Object.keys(submitData).forEach((key) => {
        if (key === "accommodation_beds") return; // ✅ skip, we append later

        if (key === "type_id") {
          fd.append(key, submitData[key]?.value || "");
        } else if (
          key === "accommodation_venue_meta" &&
          typeof submitData[key] === "object"
        ) {
          fd.append(key, JSON.stringify(submitData[key]));
        } else {
          fd.append(key, submitData[key] ?? "");
        }
      });

      // ✅ append beds only once
      fd.append("accommodation_beds", JSON.stringify(accommodationBeds));

      if (id) {
        const response = await updateAccommodationDetails(id, fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response.data?.message ||
              "Accommodation details updated successfully!",
          });
          navigate("/accommodation-detail");
        } else {
          console.error(
            "Error updating accommodation:",
            response?.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response?.data?.message || "Failed to update accommodation",
          });
        }
      } else {
        const response = await createAccommodationDetails(fd);
        if (response.data?.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text:
              response.data?.message ||
              "Accommodation details added successfully",
          });
          navigate("/accommodation-detail");
        } else {
          console.error(
            "Error updating accommodation:",
            response.data?.message || "Unknown error"
          );
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data?.message || "Failed to added accommodation",
          });
        }
      }
    } catch (err) {
      console.error(
        "Error while adding or updating bank:",
        err || "Unknown error"
      );
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err || "Failed to adding or updating bank",
      });
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const validateAddress = async () => {
  //     const apiKey = "AIzaSyCEW1Wc11SUHoF3sgsxEq0M1XgOqSelP5M";
  //     const address = "Kolkata";
  //     const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
  //       address
  //     )}&key=${apiKey}`;

  //     try {
  //       const response = await fetch(url);
  //       const data = await response.json();

  //       if (data.status === "OK" && data.results.length > 0) {
  //         console.log("✅ Valid address:", data.results[0].formatted_address);
  //         console.log("📍 Location:", data.results[0].geometry.location);
  //       } else {
  //         console.log("❌ Invalid address");
  //       }
  //     } catch (error) {
  //       console.error("Error validating address:", error);
  //     }
  //   };

  //   validateAddress();
  // }, []);

  if (loading) {
    return (
      <div id="preloader">
        <div className="sk-three-bounce">
          <div className="sk-child sk-bounce1"></div>
          <div className="sk-child sk-bounce2"></div>
          <div className="sk-child sk-bounce3"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageTitle
        activeMenu={`${id ? "Edit Accommodation Details" : "Add Accommodation Details"
          }`}
        motherMenu="Accommodation Details"
        activeMenuLink={`${id ? `edit-accommodation-detail/${id}` : "add-accommodation-detail"
          }`}
        motherMenuLink={"accommodation-detail"}
      />
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12 col-xxl-12">
              <div className="card h-auto">
                <div className="card-body">
                  <form onSubmit={handleSubmit} encType="multipart/form-data">
                    {/* Bank Logo */}
                    <div
                      onClick={() => {
                        navigate("/accommodation-detail");
                      }}
                      className="mb-4 d-flex align-items-center flex-wrap gap-2 cursor-pointer w-max"
                    >
                      <i className="fas fa-chevron-left"></i>
                      <div>Accommodation List</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Accommodation Image</label>
                      <div>
                        <img
                          src={
                            accommodationImage
                              ? URL.createObjectURL(accommodationImage)
                              : existingImage || NoImage
                          }
                          alt="Accommodation Image"
                          width={100}
                          height={100}
                          className="rounded border mb-2"
                        />
                      </div>
                      <div className="change-btn d-flex align-items-center flex-wrap">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          id="imageUpload"
                          className="d-none"
                        />
                        <label
                          htmlFor="imageUpload"
                          className="btn btn-primary ms-0"
                        >
                          Select Image
                        </label>
                      </div>
                      {errors.accommodation_image && (
                        <div className="text-danger mt-1" id="accommodation_image_error">
                          {errors.accommodation_image}
                        </div>
                      )}
                    </div>

                    {/* Accommodation Name */}
                    <div className="mb-3">
                      <label
                        className="form-label"
                        htmlFor="accommodation_title"
                      >
                        Accommodation Title
                      </label>
                      <input
                        id="accommodation_title"
                        type="text"
                        name="accommodation_title"
                        className={`form-control ${errors.accommodation_title ? "is-invalid" : ""
                          }`}
                        value={formData.accommodation_title}
                        onChange={handleChange}
                        placeholder="Enter accommodation title"
                      />
                      {errors.accommodation_title && (
                        <div
                          className="invalid-feedback"
                          id="accommodation_title_error"
                        >
                          {errors.accommodation_title}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label
                        className="form-label"
                        htmlFor="accommodation_desc"
                      >
                        Accommodation Description
                      </label>
                      <textarea
                        id="accommodation_desc"
                        name="accommodation_desc"
                        rows={2}
                        className={`form-control ${errors.accommodation_desc ? "is-invalid" : ""
                          }`}
                        value={formData.accommodation_desc}
                        onChange={handleChange}
                        placeholder="Enter accommodation description"
                      ></textarea>
                      {errors.accommodation_desc && (
                        <div
                          className="invalid-feedback"
                          id="accommodation_desc_error"
                        >
                          {errors.accommodation_desc}
                        </div>
                      )}
                    </div>

                    {/* Other details */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label
                          className="form-label"
                          htmlFor="accommodation_rate"
                        >
                          Accommodation Rate
                        </label>
                        <input
                          id="accommodation_rate"
                          type="number"
                          name="accommodation_rate"
                          className={`form-control ${errors.accommodation_rate ? "is-invalid" : ""
                            }`}
                          value={formData.accommodation_rate}
                          onChange={handleChange}
                          placeholder="Enter accommodation rate"
                        />
                        {errors.accommodation_rate && (
                          <div
                            className="invalid-feedback"
                            id="accommodation_rate_error"
                          >
                            {errors.accommodation_rate}
                          </div>
                        )}
                      </div>

                      <div className="col-md-6 mb-3 d-flex flex-column">
                        <label
                          className="form-label"
                          htmlFor="accommodation_rating"
                        >
                          Rating
                        </label>
                        <Rate
                          id="accommodation_rating"
                          allowHalf
                          value={formData.accommodation_rating}
                          onChange={handleRatingChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label
                          className="form-label"
                          htmlFor="accommodation_sleeps"
                        >
                          Accommodation Sleeps
                        </label>
                        <input
                          id="accommodation_sleeps"
                          type="text"
                          name="accommodation_sleeps"
                          className={`form-control ${errors.accommodation_sleeps ? "is-invalid" : ""
                            }`}
                          value={formData.accommodation_sleeps}
                          onChange={handleChange}
                          placeholder="Enter number of sleeps"
                        />
                        {errors.accommodation_sleeps && (
                          <div
                            className="invalid-feedback"
                            id="accommodation_sleeps_error"
                          >
                            {errors.accommodation_sleeps}
                          </div>
                        )}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label
                          className="form-label"
                          htmlFor="accommodation_carpetarea"
                        >
                          Accommodation carpet area
                        </label>
                        <input
                          id="accommodation_carpetarea"
                          type="text"
                          name="accommodation_carpetarea"
                          className={`form-control ${errors.accommodation_carpetarea ? "is-invalid" : ""
                            }`}
                          value={formData.accommodation_carpetarea}
                          onChange={handleChange}
                          placeholder="Enter carpet area"
                        />
                        {errors.accommodation_carpetarea && (
                          <div
                            className="invalid-feedback"
                            id="accommodation_carpetarea_error"
                          >
                            {errors.accommodation_carpetarea}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Type */}
                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label
                          className="form-label"
                          htmlFor="type_id"
                        >
                          Accommodation Type
                        </label>
                        <Select
                          id="type_id"
                          value={formData.type_id}
                          onChange={(value) =>
                            handleSelectChange("type_id", value)
                          }
                          options={accommodationTypeOptions}
                          placeholder="Select accommodation type"
                          closeMenuOnSelect={true}
                          menuPlacement="auto"
                          menuPosition="fixed"
                          onMenuScrollToBottom={() =>
                            fetchAccommodationTypes(accommodationTypePage + 1)
                          } // load next page
                          // onInputChange={(inputValue) => fetchCurrency(1, inputValue)} // search resets page
                          isLoading={accommodationTypeLoading}
                        />
                        {errors.type_id && (
                          <div
                            className="text-danger mt-1"
                            id="type_id_error"
                          >
                            {errors.type_id}
                          </div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label
                        className="form-label"
                        htmlFor="accommodation_venue"
                      >
                        Accommodation Venue
                      </label>
                      <Autocomplete
                        onLoad={(ref) => (autocompleteRef.current = ref)}
                        onPlaceChanged={onPlaceChanged}
                      >
                        <input
                          type="text"
                          name="accommodation_venue"
                          placeholder="Enter Venue"
                          className="autocomplete-input mb-3"
                          value={formData.accommodation_venue}
                          onChange={handleChange}
                        />
                      </Autocomplete>

                      <GoogleMap
                        mapContainerStyle={containerStyle}
                        center={mapCenter}
                        zoom={18}
                      >
                        {markerPosition && <Marker position={markerPosition} />}
                      </GoogleMap>
                      {errors.accommodation_venue && (
                        <div
                          className="text-danger mt-1"
                          id="accommodation_venue_error"
                        >
                          {errors.accommodation_venue}
                        </div>
                      )}
                    </div>

                    <hr style={{ margin: "0px" }} className="mx-2" />
                    <div className="card-footer border-0 text-end py-3 d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate("/bank")}
                      >
                        Back
                      </Button>

                      {/* Save / Update Button */}
                      <Button type="submit" className="btn btn-primary btn-sm">
                        {id ? "Update" : "Save"}
                      </Button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddAccommodationDetails;
